import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Zone, ZoneInsert, ZoneUpdate, Domain, PurdueLevel, Criticality, PURDUE_LEVELS } from '@/types/zone';
import { X, Plus, Info, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { validateCIDR, validateVLAN, isValidVLAN, isValidCIDR } from '@/utils/networkValidation';
import { UserSelector } from '@/components/shared/UserSelector';

interface ZoneFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (zone: ZoneInsert | ZoneUpdate) => Promise<void>;
  zone?: Zone;
  siteId?: string;
  sites?: Array<{ id: string; name: string }>;
}

export function ZoneFormDialog({ open, onOpenChange, onSubmit, zone, siteId, sites: _sites = [] }: ZoneFormDialogProps) {
  const [name, setName] = useState(zone?.name || '');
  const [domain, setDomain] = useState<Domain>(zone?.domain || 'IT');
  const [purdueLevel, setPurdueLevel] = useState<PurdueLevel | undefined>(zone?.purdue_level);
  const [cidrs, setCidrs] = useState<string[]>(zone?.cidrs || ['']);
  const [cidrErrors, setCidrErrors] = useState<(string | undefined)[]>([]);
  const [vlanId, setVlanId] = useState<string>(zone?.vlan_id?.toString() || '');
  const [vlanError, setVlanError] = useState<string | undefined>();
  const [criticality, setCriticality] = useState<Criticality | undefined>(zone?.criticality);
  const [description, setDescription] = useState(zone?.description || '');
  const [dataClassification, setDataClassification] = useState(zone?.data_classification || '');
  const [backupFrequency, setBackupFrequency] = useState(zone?.backup_frequency || '');
  const [selectedSiteId, setSelectedSiteId] = useState<string>(siteId || zone?.site_id || '');
  const [ownerId, setOwnerId] = useState<string | undefined>(zone?.owner_id);
  const [loading, setLoading] = useState(false);

  // Sync selectedSiteId when siteId prop changes
  useEffect(() => {
    if (siteId) {
      setSelectedSiteId(siteId);
    }
  }, [siteId]);

  const handleAddCidr = () => {
    setCidrs([...cidrs, '']);
  };

  const handleRemoveCidr = (index: number) => {
    setCidrs(cidrs.filter((_, i) => i !== index));
    const newErrors = [...cidrErrors];
    newErrors.splice(index, 1);
    setCidrErrors(newErrors);
  };

  const handleCidrChange = (index: number, value: string) => {
    const newCidrs = [...cidrs];
    newCidrs[index] = value;
    setCidrs(newCidrs);
    
    // Validate CIDR on change
    const newErrors = [...cidrErrors];
    if (value.trim()) {
      const result = validateCIDR(value);
      newErrors[index] = result.valid ? undefined : result.message;
    } else {
      newErrors[index] = undefined;
    }
    setCidrErrors(newErrors);
  };

  const handleVlanChange = (value: string) => {
    setVlanId(value);
    if (value.trim()) {
      const result = validateVLAN(value);
      setVlanError(result.valid ? undefined : result.message);
    } else {
      setVlanError(undefined);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const filteredCidrs = cidrs.filter(c => c.trim() !== '');
      
      // Validate all CIDRs
      const invalidCidrs = filteredCidrs.filter(c => !isValidCIDR(c));
      if (invalidCidrs.length > 0) {
        toast.error('Please fix invalid CIDR formats');
        setLoading(false);
        return;
      }
      
      // Validate VLAN if provided
      if (vlanId.trim() && !isValidVLAN(vlanId)) {
        toast.error('Invalid VLAN ID (must be 1-4094)');
        setLoading(false);
        return;
      }
      
      // Validate that we have a site_id for new zones
      if (!zone && !selectedSiteId) {
        toast.error('Please select a site');
        setLoading(false);
        return;
      }
      
      const parsedVlanId = vlanId.trim() ? parseInt(vlanId, 10) : undefined;
      
      if (zone) {
        await onSubmit({
          name,
          domain,
          purdue_level: purdueLevel,
          cidrs: filteredCidrs.length > 0 ? filteredCidrs : undefined,
          vlan_id: parsedVlanId,
          criticality,
          description: description || undefined,
          data_classification: dataClassification || undefined,
          backup_frequency: backupFrequency || undefined,
          owner_id: ownerId,
        });
      } else {
        await onSubmit({
          site_id: selectedSiteId,
          org_id: '', // Will be set by the store
          name,
          domain,
          purdue_level: purdueLevel,
          cidrs: filteredCidrs.length > 0 ? filteredCidrs : undefined,
          vlan_id: parsedVlanId,
          criticality,
          description: description || undefined,
          data_classification: dataClassification || undefined,
          backup_frequency: backupFrequency || undefined,
          owner_id: ownerId,
        });
      }
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{zone ? 'Edit Zone' : 'Create Zone'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="domain">Domain *</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-medium mb-2">Network Domain Classification</p>
                      <ul className="space-y-1 text-xs">
                        <li><strong>IT:</strong> Corporate information systems (email, ERP, databases)</li>
                        <li><strong>OT:</strong> Industrial control systems (SCADA, PLCs, HMIs)</li>
                        <li><strong>Cloud:</strong> Cloud infrastructure and services</li>
                        <li><strong>DMZ:</strong> Buffer zone between internal and external networks</li>
                        <li><strong>IoT:</strong> Internet of Things devices</li>
                        <li><strong>Physical:</strong> Physical security zones (cameras, access control)</li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Select value={domain} onValueChange={(v) => setDomain(v as Domain)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IT">IT</SelectItem>
                  <SelectItem value="OT">OT</SelectItem>
                  <SelectItem value="Cloud">Cloud</SelectItem>
                  <SelectItem value="DMZ">DMZ</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="purdue">Purdue Level</Label>
              <Select value={purdueLevel} onValueChange={(v) => setPurdueLevel(v as PurdueLevel)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  {PURDUE_LEVELS.map((level) => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Network Configuration */}
          <div className="space-y-4 p-3 border rounded-md bg-muted/30">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Info className="h-4 w-4" />
              Network Configuration
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vlan">VLAN ID</Label>
                <div className="relative">
                  <Input
                    id="vlan"
                    type="number"
                    min="1"
                    max="4094"
                    placeholder="1-4094"
                    value={vlanId}
                    onChange={(e) => handleVlanChange(e.target.value)}
                    className={vlanError ? 'border-destructive pr-10' : vlanId && !vlanError ? 'border-green-500 pr-10' : ''}
                  />
                  {vlanId && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {vlanError ? (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {vlanError && <p className="text-xs text-destructive">{vlanError}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>CIDR Blocks (Subnets)</Label>
                <Button type="button" size="sm" variant="outline" onClick={handleAddCidr}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add CIDR
                </Button>
              </div>
              <div className="space-y-2">
                {cidrs.map((cidr, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          placeholder="e.g., 192.168.1.0/24"
                          value={cidr}
                          onChange={(e) => handleCidrChange(index, e.target.value)}
                          className={cidrErrors[index] ? 'border-destructive pr-10' : cidr && !cidrErrors[index] ? 'border-green-500 pr-10' : ''}
                        />
                        {cidr && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {cidrErrors[index] ? (
                              <AlertCircle className="h-4 w-4 text-destructive" />
                            ) : (
                              <Check className="h-4 w-4 text-green-500" />
                            )}
                          </div>
                        )}
                      </div>
                      {cidrs.length > 1 && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => handleRemoveCidr(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {cidrErrors[index] && <p className="text-xs text-destructive">{cidrErrors[index]}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="criticality">Criticality</Label>
              <Select value={criticality} onValueChange={(v) => setCriticality(v as Criticality)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select criticality" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Very High">Very High</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Very Low">Very Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="backup">Backup Frequency</Label>
              <Input
                id="backup"
                placeholder="e.g., Daily, Weekly"
                value={backupFrequency}
                onChange={(e) => setBackupFrequency(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="classification">Data Classification</Label>
            <Input
              id="classification"
              placeholder="e.g., Confidential, Internal"
              value={dataClassification}
              onChange={(e) => setDataClassification(e.target.value)}
            />
          </div>

          <UserSelector
            value={ownerId}
            onChange={setOwnerId}
            label="Owner"
            placeholder="Select owner"
          />

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : zone ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
